import React, { useEffect, useState } from 'react';
import { ReceiptMetadata, getReceiptPreviewUrl, updateReceipt } from '../services/api';
import { useApiToken } from '../auth/AuthProvider';

interface Props {
  receipt: ReceiptMetadata | null;
  onClose: () => void;
  onUpdate?: (updated: ReceiptMetadata) => void;
  // When set, bypasses getReceiptPreviewUrl (used for admin/route previews).
  previewUrl?: string | null;
  // When true, hides the amount edit/save UI (e.g. admin read-only view).
  readOnly?: boolean;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 24,
  },
  modal: {
    background: 'white',
    borderRadius: 12,
    maxWidth: 900,
    maxHeight: '90vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f7fafc',
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  vendor: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1a365d',
  },
  detail: {
    fontSize: 12,
    color: '#666',
  },
  amount: {
    fontSize: 15,
    fontWeight: 700,
    color: '#38a169',
    cursor: 'pointer',
    borderBottom: '1px dashed #38a169',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: 28,
    color: '#666',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
  body: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f0f0',
    minHeight: 400,
  },
  image: {
    maxWidth: '100%',
    maxHeight: '75vh',
    objectFit: 'contain' as const,
  },
  iframe: {
    width: '100%',
    height: '75vh',
    border: 'none',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    borderTop: '1px solid #e2e8f0',
    background: '#f7fafc',
  },
  fileName: {
    fontSize: 12,
    color: '#888',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: '60%',
  },
  openBtn: {
    background: '#0078d4',
    color: 'white',
    border: 'none',
    padding: '6px 14px',
    borderRadius: 4,
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500,
    textDecoration: 'none',
  },
  editInput: {
    width: 90,
    padding: '4px 8px',
    border: '2px solid #0078d4',
    borderRadius: 4,
    fontSize: 15,
    fontWeight: 700,
    color: '#1a365d',
    textAlign: 'right' as const,
  },
};

export default function ReceiptPreviewModal({ receipt, onClose, onUpdate, previewUrl: previewUrlOverride, readOnly }: Props) {
  const getToken = useApiToken();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editAmount, setEditAmount] = useState('');
  const [saving, setSaving] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Build backend proxy URL for preview (bypasses Azure Blob CORS for both PDFs and images).
  // An explicit previewUrlOverride short-circuits the user-scoped fetch (used by admin/route previews).
  useEffect(() => {
    if (!receipt) { setPreviewUrl(null); return; }
    if (previewUrlOverride) { setPreviewUrl(previewUrlOverride); return; }

    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!cancelled) setPreviewUrl(getReceiptPreviewUrl(token, receipt.receipt_id));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [receipt?.receipt_id, getToken, previewUrlOverride]);

  // Reset edit state when receipt changes
  useEffect(() => {
    setEditing(false);
    setEditAmount('');
  }, [receipt?.receipt_id]);

  if (!receipt) return null;

  const fileName = receipt.file_name.toLowerCase();
  const useIframe = fileName.endsWith('.pdf') || fileName.endsWith('.html');
  const dateStr = receipt.receipt_date || 'Date pending';
  const timeStr = receipt.receipt_time ? ` at ${receipt.receipt_time}` : '';

  const handleStartEdit = () => {
    setEditing(true);
    setEditAmount(receipt.receipt_amount != null ? receipt.receipt_amount.toFixed(2) : '');
  };

  const handleSaveAmount = async () => {
    const amount = parseFloat(editAmount);
    if (isNaN(amount)) { setEditing(false); return; }
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await updateReceipt(token, receipt.receipt_id, { receipt_amount: amount });
      onUpdate?.(updated);
      setEditing(false);
    } catch (e) {
      alert(`Failed to save: ${e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSaveAmount();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.headerInfo}>
            <span style={styles.vendor}>{receipt.vendor_name || receipt.file_name}</span>
            <span style={styles.detail}>
              {dateStr}{timeStr}
              {receipt.is_directions && receipt.total_mileage && ` \u00b7 ${receipt.total_mileage} miles`}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {readOnly ? (
              receipt.receipt_amount != null && (
                <span style={{ fontSize: 15, fontWeight: 700, color: '#38a169' }}>
                  ${receipt.receipt_amount.toFixed(2)}
                </span>
              )
            ) : (
              <>
                {editing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1a365d' }}>$</span>
                    <input
                      style={styles.editInput}
                      type="number"
                      step="0.01"
                      value={editAmount}
                      onChange={(e) => setEditAmount(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      onBlur={handleSaveAmount}
                      autoFocus
                      disabled={saving}
                    />
                  </div>
                ) : (
                  receipt.receipt_amount != null && (
                    <span
                      style={styles.amount}
                      onClick={handleStartEdit}
                      title="Click to edit amount"
                    >
                      ${receipt.receipt_amount.toFixed(2)}
                    </span>
                  )
                )}
                {receipt.receipt_amount == null && !editing && (
                  <button
                    style={{ background: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#333' }}
                    onClick={handleStartEdit}
                  >
                    Add Amount
                  </button>
                )}
              </>
            )}
            <button style={styles.closeBtn} onClick={onClose} title="Close">&times;</button>
          </div>
        </div>

        <div style={styles.body}>
          {useIframe ? (
            previewUrl ? (
              <iframe
                src={previewUrl}
                style={styles.iframe}
                title={receipt.file_name}
              />
            ) : (
              <p style={{ color: '#666', fontSize: 14 }}>Loading PDF preview...</p>
            )
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt={receipt.file_name}
              style={styles.image}
            />
          ) : (
            <p style={{ color: '#666', fontSize: 14 }}>Loading preview...</p>
          )}
        </div>

        <div style={styles.footer}>
          <span style={styles.fileName} title={receipt.file_name}>{receipt.file_name}</span>
          {previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              style={styles.openBtn}
            >
              Open in New Tab
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
